import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { Typography, Button, CircularProgress, Link } from '@mui/material';
import { useAuthFormToast } from '../components/auth/useAuthFormToast';
import { useAuth } from '../contexts/AuthContext';
import { AuthPageShell } from '../components/auth/AuthPageShell';
import { authLinkSx, authPrimaryButtonSx } from '../components/auth/authFormStyles';
import { deriveProfileComplete } from '../utils/profileCompletion';

export const MagicLinkPage: React.FC = () => {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { verifyMagicLinkAndLogin } = useAuth();
  const { showError, ToastHost } = useAuthFormToast();

  const token = searchParams.get('token');
  const email = searchParams.get('email');

  useEffect(() => {
    if (!token || !email) {
      setErrorMessage('This link is incomplete or invalid. Please request a new one.');
      setStatus('error');
      return;
    }

    verifyMagicLinkAndLogin(token, email)
      .then((clientData) => {
        setStatus('success');
        if (!deriveProfileComplete(clientData)) {
          navigate('/complete-profile', { replace: true });
        } else {
          navigate('/', { replace: true });
        }
      })
      .catch((err: unknown) => {
        const message =
          err instanceof Error ? err.message : 'This link has expired or already been used.';
        setErrorMessage(message);
        setStatus('error');
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (status === 'error' && errorMessage) {
      showError(errorMessage);
    }
  }, [status, errorMessage, showError]);

  return (
    <>
    <AuthPageShell title="Email verification" subtitle="Hang tight while we confirm your account.">
      {status === 'loading' && (
        <>
          <CircularProgress sx={{ mb: 2, color: '#FF0100' }} />
          <Typography variant="body1" color="text.secondary">
            Verifying your email…
          </Typography>
        </>
      )}

      {status === 'error' && (
        <>
          <Typography variant="body2" sx={{ mb: 3, color: '#565959', textAlign: 'center' }}>
            We couldn&apos;t verify this link. Use the options below to continue.
          </Typography>
          <Button fullWidth variant="contained" onClick={() => navigate('/signup')} sx={{ ...authPrimaryButtonSx, mb: 1.5 }}>
            Back to sign up
          </Button>
          <Link component={RouterLink} to="/login" sx={authLinkSx}>
            Back to sign in
          </Link>
        </>
      )}

      {status === 'success' && (
        <Typography variant="body1" color="text.secondary">
          Redirecting…
        </Typography>
      )}
    </AuthPageShell>
    <ToastHost />
    </>
  );
};
